import React, { useEffect, useState } from "react";
import axios from "axios";
import "./GroupStyle.css";
import { useParams } from "react-router-dom";
import { Table, Container, Badge, Spinner } from "react-bootstrap";
import PageLayout from "../../layouts/PageLayout";
import { Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";

const LOAN_KEYS = [
	"total_loans",
	"loan_amount",
	"members_with_loans",
	"small_business",
	"rickshaw",
];

const MEMBER_KEYS = ["total_members", "male_members", "female_members"];

// Keys whose detail data is { sum|average, meetings: [...] }
const MEETING_KEYS = ["total_savings", "avg_saving"];

const formatMoney = (amount) =>
	`PKR ${Number(amount || 0).toLocaleString("en-PK")}`;

const formatDate = (date) => {
	if (!date) {
		return "-";
	}

	const parsedDate = new Date(date);

	return isNaN(parsedDate.getTime())
		? date
		: parsedDate.toLocaleDateString("en-GB");
};

const getGender = (gender) => {
	if (gender === 1 || gender === "1") {
		return "Male";
	}

	if (gender === 2 || gender === "2") {
		return "Female";
	}

	return gender || "-";
};

// Booleans/"1"/"0" render as nothing in React, so show Yes/No
const yesNo = (value) =>
	value === true || value === 1 || value === "1" ? (
		<Badge bg="success">Yes</Badge>
	) : (
		<Badge bg="secondary">No</Badge>
	);

const getMobilizers = (mobilizers) => {
	if (Array.isArray(mobilizers)) {
		return mobilizers.length ? mobilizers.join(", ") : "-";
	}

	return mobilizers || "-";
};

function GroupsList() {
	const { key } = useParams(); // Example: total_groups
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);

	const isLoanKey = LOAN_KEYS.includes(key);
	const isMemberKey = MEMBER_KEYS.includes(key);
	const isMeetingKey = MEETING_KEYS.includes(key);

	useEffect(() => {
		const AuthToken = localStorage.getItem("AuthUser");
		axios.defaults.headers.common["Authorization"] = `Bearer ${AuthToken}`;

		setLoading(true);

		axios
			.get(`${process.env.REACT_APP_API_URL}/act-group-details/${key}`)
			.then((response) => {
				if (response.data.success) {
					const data = response.data.data[key];

					if (MEETING_KEYS.includes(key)) {
						setRows(data?.meetings || []);
					} else {
						setRows(Array.isArray(data) ? data : []);
					}
				}
			})
			.catch((error) => {
				console.error(error);
				setRows([]);
			})
			.finally(() => setLoading(false));
	}, [key]);

	const title = isLoanKey
		? "Loans"
		: isMemberKey
		? "Members"
		: isMeetingKey
		? "Meetings"
		: "Groups";

	const emptyRow = (colSpan, text) => (
		<tr>
			<td colSpan={colSpan} style={{ textAlign: "center" }}>
				{loading ? <Spinner animation="border" size="sm" /> : text}
			</td>
		</tr>
	);

	const viewGroupLink = (groupId) => (
		// Read-only admin view (no Edit / Give Loan)
		<Link to={`/view-group-members/${groupId}`} title="View Group">
			<i
				className="material-icons view view-icon"
				style={{
					color: "white",
					borderRadius: "5px",
					backgroundColor: "rgba(7, 120, 52, 0.7)",
					padding: "2px 2px",
				}}
			>
				visibility
			</i>
		</Link>
	);

	/*
	|--------------------------------------------------------------------------
	| LOANS TABLE
	|--------------------------------------------------------------------------
	*/

	const renderLoans = () => (
		<Table className="mc-table product" striped bordered hover>
			<thead className="mc-table-head ">
				<tr>
					<th>#</th>
					<th>Group</th>
					<th>Member</th>
					<th>Amount</th>
					<th>Date</th>
					<th>Time Period</th>
					<th>Purpose</th>
					<th>Evidence</th>
				</tr>
			</thead>
			<tbody className="mc-table-body even">
				{rows.length > 0
					? rows.map((loan, index) => (
							<tr key={loan.id}>
								<td>{index + 1}</td>
								<td>
									<Link to={`/act-groups/loans/${loan.group_id}`}>
										{loan.group?.group_name || "-"}
										{loan.group?.group_code ? ` (${loan.group.group_code})` : ""}
									</Link>
								</td>
								<td>{loan.member?.name || "-"}</td>
								<td>{formatMoney(loan.amount)}</td>
								<td>{formatDate(loan.loan_date)}</td>
								<td>{loan.time_period || "-"}</td>
								<td>{loan.purpose_text || loan.purpose}</td>
								<td>
									{loan.evidence ? (
										<a
											href={`${process.env.REACT_APP_IMG_URL || ""}${loan.evidence}`}
											target="_blank"
											rel="noreferrer"
										>
											View
										</a>
									) : (
										"-"
									)}
								</td>
							</tr>
					  ))
					: emptyRow(8, "No loans found")}
			</tbody>
		</Table>
	);

	/*
	|--------------------------------------------------------------------------
	| MEMBERS TABLE
	|--------------------------------------------------------------------------
	*/

	const renderMembers = () => (
		<Table className="mc-table product" striped bordered hover>
			<thead className="mc-table-head ">
				<tr>
					<th>#</th>
					<th>Name</th>
					<th>Role</th>
					<th>CNIC</th>
					<th>Mobile</th>
					<th>Group</th>
					<th>Status</th>
					<th>Actions</th>
				</tr>
			</thead>
			<tbody className="mc-table-body even">
				{rows.length > 0
					? rows.map((member, index) => (
							<tr key={member.id}>
								<td>{index + 1}</td>
								<td>{member.name || "-"}</td>
								<td style={{ textTransform: "capitalize" }}>{member.role || "-"}</td>
								<td>{member.cnic || "-"}</td>
								<td>{member.mobile || "-"}</td>
								<td>
									{member.group?.group_name || "-"}
									{member.group?.group_code ? ` (${member.group.group_code})` : ""}
								</td>
								<td>
									{member.status ? (
										<Badge bg="success">Active</Badge>
									) : (
										<Badge bg="danger">Inactive</Badge>
									)}
								</td>
								<td className="eye-icon">{viewGroupLink(member.group_id)}</td>
							</tr>
					  ))
					: emptyRow(8, "No members found")}
			</tbody>
		</Table>
	);

	/*
	|--------------------------------------------------------------------------
	| MEETINGS TABLE (savings cards)
	|--------------------------------------------------------------------------
	*/

	const renderMeetings = () => (
		<Table className="mc-table product" striped bordered hover>
			<thead className="mc-table-head ">
				<tr>
					<th>#</th>
					<th>Group</th>
					<th>Meeting Date</th>
					<th>Collected Amount</th>
					<th>Notes</th>
					<th>Actions</th>
				</tr>
			</thead>
			<tbody className="mc-table-body even">
				{rows.length > 0
					? rows.map((meeting, index) => (
							<tr key={meeting.id}>
								<td>{index + 1}</td>
								<td>
									{meeting.group?.group_name || "-"}
									{meeting.group?.group_code ? ` (${meeting.group.group_code})` : ""}
								</td>
								<td>{formatDate(meeting.date)}</td>
								<td>{formatMoney(meeting.collected_amount)}</td>
								<td>{meeting.notes || "-"}</td>
								<td className="eye-icon">{viewGroupLink(meeting.group_id)}</td>
							</tr>
					  ))
					: emptyRow(6, "No meetings found")}
			</tbody>
		</Table>
	);

	/*
	|--------------------------------------------------------------------------
	| GROUPS TABLE
	|--------------------------------------------------------------------------
	*/

	const renderGroups = () => (
		<Table className="mc-table product" striped bordered hover>
			<thead className="mc-table-head ">
				<tr>
					<th>#</th>
					<th>Group Code</th>
					<th>Group Name</th>
					<th>City</th>
					<th>Address</th>
					<th>Gender</th>
					<th>Date</th>
					<th>Agreed Amount</th>
					<th>Saving Frequency</th>
					<th>Meeting Frequency</th>
					<th>Mobilizers</th>
					<th>Admission Form</th>
					<th>Saving Box</th>
					<th>Minute Book</th>
					<th>Loan Ledger</th>
					<th>Pass Book</th>
					<th>Added By</th>
					<th>Actions</th>
				</tr>
			</thead>
			<tbody className="mc-table-body even">
				{rows.length > 0
					? rows.map((group, index) => (
							<tr key={group.id}>
								<td>{index + 1}</td>
								<td>{group.group_code || "-"}</td>
								<td>{group.group_name || "-"}</td>
								<td>{group.city?.name || "-"}</td>
								<td>{group.address || "-"}</td>
								<td>{getGender(group.group_gender)}</td>
								<td>{formatDate(group.date)}</td>
								<td>{formatMoney(group.agreed_amount)}</td>
								<td>{group.saving_frequency || "-"}</td>
								<td>{group.meeting_frequency || "-"}</td>
								<td>{getMobilizers(group.mobilizers)}</td>
								<td>{yesNo(group.admission_from_status)}</td>
								<td>{yesNo(group.saving_box_delivered)}</td>
								<td>{yesNo(group.minute_book_delivered)}</td>
								<td>{yesNo(group.loan_ledger_delivered)}</td>
								<td>{yesNo(group.pass_book_delivered)}</td>
								<td>{group.user?.name || "-"}</td>
								<td className="eye-icon">{viewGroupLink(group.id)}</td>
							</tr>
					  ))
					: emptyRow(18, "No groups found")}
			</tbody>
		</Table>
	);

	return (
		<>
			<PageLayout>
				<Row>
					<Col xl={12}>
						<div className="mc-card">
							<div className="mc-breadcrumb">
								<ul className="mc-breadcrumb-list">
									<li className="mc-breadcrumb-item">
										<Link
											to="/"
											className="mc-breadcrumb-link"
											style={{ color: "#077834" }}
										>
											{"home"}
										</Link>
									</li>

									<li className="mc-breadcrumb-item">
										<Link
											to="/act-groups-dashboard"
											className="mc-breadcrumb-link"
											style={{ color: "#077834" }}
										>
											{"ACT Group Dashboard"}
										</Link>
									</li>

									<li className="mc-breadcrumb-item">{title}</li>
								</ul>
							</div>
						</div>
					</Col>
				</Row>
				<Container className="mt-4">
					<div style={{ overflowX: "auto" }}>
						{isLoanKey
							? renderLoans()
							: isMemberKey
							? renderMembers()
							: isMeetingKey
							? renderMeetings()
							: renderGroups()}
					</div>
				</Container>
			</PageLayout>
		</>
	);
}

export default GroupsList;
